import { useEffect, useRef, useState } from 'react';

// Shared IntersectionObserver instance to reduce overhead
let sharedObserver = null;
const observerCallbacks = new Map();

const getSharedObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const callback = observerCallbacks.get(entry.target);
          if (callback) {
            callback(entry.isIntersecting);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before it's fully visible
        threshold: 0.01, // Trigger when 1% of the item is visible
      }
    );
  }
  return sharedObserver;
};

/**
 * Custom hook that uses a shared IntersectionObserver instance
 * to detect when an element enters the viewport.
 * 
 * @param {boolean} skipObserver - If true, immediately sets isVisible to true (for priority items)
 * @returns {[React.RefObject, boolean]} - [ref to attach to element, isVisible state]
 */
const useIntersectionObserver = (skipObserver = false) => {
  const [isVisible, setIsVisible] = useState(skipObserver);
  const elementRef = useRef(null);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // If skipObserver is true, mark as visible immediately
    if (skipObserver) {
      setIsVisible(true);
      hasCheckedRef.current = true;
      return undefined;
    }

    // If already visible, don't set up observer again
    if (isVisible && hasCheckedRef.current) {
      return undefined;
    }

    const element = elementRef.current;
    if (!element) return undefined;

    // Check if element is already in viewport (synchronous check)
    // This handles items that are visible when filter is applied
    const rect = element.getBoundingClientRect();
    const isInViewport = (
      rect.top < window.innerHeight + 100 && // Add 100px buffer (matches rootMargin)
      rect.bottom > -100 &&
      rect.left < window.innerWidth &&
      rect.right > 0
    );

    if (isInViewport) {
      setIsVisible(true);
      hasCheckedRef.current = true;
      return undefined;
    }

    // Mark that we've checked, even if not visible yet
    hasCheckedRef.current = true;

    const observer = getSharedObserver();
    
    // Store callback for this element
    observerCallbacks.set(element, (isIntersecting) => {
      if (isIntersecting) {
        setIsVisible(true);
        // Once visible, we can stop observing
        observer.unobserve(element);
        observerCallbacks.delete(element);
      }
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observerCallbacks.delete(element);
    };
  }, [skipObserver, isVisible]);

  return [elementRef, isVisible];
};

export default useIntersectionObserver;
